import { Routes, Route, useLocation, Navigate } from "react-router-dom"
import { useEffect } from "react"
import { Analytics } from "@vercel/analytics/react"
import { SpeedInsights } from "@vercel/speed-insights/react"
import ErrorBoundary from "./components/ErrorBoundary"
import LandingPage from "./pages/LandingPage"
import LoginPage from "./components/LoginPage"
import SignupPage from "./components/SignupPage"
import ForgotPasswordPage from "./pages/ForgotPasswordPage"
import ResetPasswordPage from "./pages/ResetPasswordPage"
import DisclaimerPage from "./pages/DisclaimerPage"
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage"
import TermsAndConditionsPage from "./pages/TermsAndConditionsPage"
import SupportPage from "./pages/SupportPage"
import AppDashboard from "./components/AppDashboard"
import { AuthLoadingScreen, useAuth } from "./components/AuthProvider"
import OAuthCallbackHandler from "./components/OAuthCallbackHandler"
import { setSentryViewTags } from "./utils/sentryTracking"
import { useRedirectIfAuthenticated } from "./hooks/useRedirectIfAuthenticated"

function Shell({ children }) {
  return (
    <div className="min-h-screen w-full bg-white text-neutral-900 antialiased">{children}</div>
  )
}

/** Legal pages keep a tiny header so visitors can return home. */
function LegalLayout({ children }) {
  useRedirectIfAuthenticated()
  return (
    <Shell>
      <header className="border-b border-neutral-200 px-6 py-4">
        <a href="/" className="text-sm text-neutral-600 hover:text-neutral-900">
          ← LandSentry
        </a>
      </header>
      <main className="w-full">{children}</main>
    </Shell>
  )
}

/** Preserve OAuth/password-recovery params when rewriting old /app auth URLs. */
function LegacyPublicAuthRedirect({ to }) {
  const { search, hash } = useLocation()
  return <Navigate to={`${to}${search}${hash}`} replace />
}

/** Keep the authenticated shell from mounting before auth is resolved. */
function AuthenticatedApp() {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return <AuthLoadingScreen />
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />

  return <AppDashboard />
}

function SentryRouteTracker() {
  const location = useLocation()

  useEffect(() => {
    setSentryViewTags({ pathname: location.pathname })
  }, [location.pathname])

  return null
}

function App() {
  return (
    <div className="min-h-screen w-full">
      <SentryRouteTracker />
      <OAuthCallbackHandler />
      <ErrorBoundary>
        <Routes>
          <Route
            path="/"
            element={
              <Shell>
                <LandingPage />
              </Shell>
            }
          />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route
            path="/disclaimer"
            element={
              <LegalLayout>
                <DisclaimerPage />
              </LegalLayout>
            }
          />
          <Route
            path="/privacy-policy"
            element={
              <LegalLayout>
                <PrivacyPolicyPage />
              </LegalLayout>
            }
          />
          <Route
            path="/terms-and-conditions"
            element={
              <LegalLayout>
                <TermsAndConditionsPage />
              </LegalLayout>
            }
          />
          <Route
            path="/support"
            element={
              <LegalLayout>
                <SupportPage />
              </LegalLayout>
            }
          />

          {/* Legacy auth URLs under /app → public top-level paths */}
          <Route path="/app/login" element={<LegacyPublicAuthRedirect to="/login" />} />
          <Route path="/app/signup" element={<LegacyPublicAuthRedirect to="/signup" />} />
          <Route path="/app/forgot-password" element={<LegacyPublicAuthRedirect to="/forgot-password" />} />
          <Route path="/app/reset-password" element={<LegacyPublicAuthRedirect to="/reset-password" />} />

          <Route path="/app/*" element={<AuthenticatedApp />} />

          <Route
            path="*"
            element={
              <Shell>
                <LandingPage />
              </Shell>
            }
          />
        </Routes>
      </ErrorBoundary>
      <Analytics />
      <SpeedInsights />
    </div>
  )
}

export default App
